import React, { useState } from 'react';
import { ShieldCheckIcon, ClockIcon, PhoneIcon, SendIcon } from 'lucide-react';
interface ServiceContactFormProps {
  serviceName: string;
}
export function ServiceContactForm({
  serviceName
}: ServiceContactFormProps) {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitted(true);
    setTimeout(() => setIsSubmitted(false), 5000);
  };
  return <section className="w-full bg-soft-teal py-16 lg:py-24" id="contact-form" data-id="element-372">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-373">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-374">
          {/* Left: Trust Signals & Copy */}
          <div data-id="element-375">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-text-dark tracking-tight mb-4" data-id="element-376">
              Request <span className="text-medical-blue" data-id="element-377">{serviceName}</span>
            </h2>
            <p className="text-lg text-text-muted mb-10 leading-relaxed" data-id="element-378">
              Fill out the form below to get connected with our vetted
              healthcare professionals. Our team will review your request and
              match you with the right fit in minutes.
            </p>

            <div className="space-y-6" data-id="element-379">
              <div className="flex items-start gap-4" data-id="element-380">
                <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center flex-shrink-0" data-id="element-381">
                  <ShieldCheckIcon className="w-6 h-6 text-accent-teal" data-id="element-382" />
                </div>
                <div data-id="element-383">
                  <h3 className="font-bold text-text-dark text-lg mb-1" data-id="element-384">
                    Nurse-Vetted Staff
                  </h3>
                  <p className="text-text-muted text-sm leading-relaxed" data-id="element-385">
                    Every professional is thoroughly screened,
                    credential-verified, and clinically assessed by our RN
                    leadership team.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4" data-id="element-386">
                <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center flex-shrink-0" data-id="element-387">
                  <ClockIcon className="w-6 h-6 text-accent-teal" data-id="element-388" />
                </div>
                <div data-id="element-389">
                  <h3 className="font-bold text-text-dark text-lg mb-1" data-id="element-390">
                    4-Min Average Response
                  </h3>
                  <p className="text-text-muted text-sm leading-relaxed" data-id="element-391">
                    Our real-time matching algorithm connects you with
                    available, qualified staff faster than traditional agencies.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4" data-id="element-392">
                <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center flex-shrink-0" data-id="element-393">
                  <PhoneIcon className="w-6 h-6 text-accent-teal" data-id="element-394" />
                </div>
                <div data-id="element-395">
                  <h3 className="font-bold text-text-dark text-lg mb-1" data-id="element-396">
                    24/7 Support Available
                  </h3>
                  <p className="text-text-muted text-sm leading-relaxed" data-id="element-397">
                    Healthcare never sleeps, and neither do we. Our support team
                    is available around the clock for urgent requests.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Form Card */}
          <div className="bg-white rounded-2xl shadow-float p-6 sm:p-8 border border-gray-100" data-id="element-398">
            {isSubmitted ? <div className="text-center py-12" data-id="element-399">
                <div className="w-16 h-16 bg-soft-teal rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-400">
                  <ShieldCheckIcon className="w-8 h-8 text-accent-teal" data-id="element-401" />
                </div>
                <h3 className="text-2xl font-bold text-text-dark mb-2" data-id="element-402">
                  Request Received!
                </h3>
                <p className="text-text-muted" data-id="element-403">
                  Thank you for your interest in our {serviceName}. Our team
                  will be in touch shortly.
                </p>
              </div> : <form onSubmit={handleSubmit} className="space-y-5" data-id="element-404">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-405">
                  <div data-id="element-406">
                    <label htmlFor="fullName" className="block text-sm font-semibold text-text-dark mb-1.5" data-id="element-407">
                      Full Name
                    </label>
                    <input type="text" id="fullName" required className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-medical-blue focus:ring-2 focus:ring-medical-blue/20 outline-none transition-all text-sm" placeholder="Jane Doe" data-id="element-408" />
                  </div>
                  <div data-id="element-409">
                    <label htmlFor="phone" className="block text-sm font-semibold text-text-dark mb-1.5" data-id="element-410">
                      Phone Number
                    </label>
                    <input type="tel" id="phone" required className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-medical-blue focus:ring-2 focus:ring-medical-blue/20 outline-none transition-all text-sm" placeholder="(555) 123-4567" data-id="element-411" />
                  </div>
                </div>

                <div data-id="element-412">
                  <label htmlFor="email" className="block text-sm font-semibold text-text-dark mb-1.5" data-id="element-413">
                    Email Address
                  </label>
                  <input type="email" id="email" required className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-medical-blue focus:ring-2 focus:ring-medical-blue/20 outline-none transition-all text-sm" placeholder="jane@example.com" data-id="element-414" />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-415">
                  <div data-id="element-416">
                    <label htmlFor="service" className="block text-sm font-semibold text-text-dark mb-1.5" data-id="element-417">
                      Service Type
                    </label>
                    <input type="text" id="service" value={serviceName} readOnly className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-text-muted outline-none text-sm font-medium cursor-not-allowed" data-id="element-418" />
                  </div>
                  <div data-id="element-419">
                    <label htmlFor="date" className="block text-sm font-semibold text-text-dark mb-1.5" data-id="element-420">
                      Preferred Start Date
                    </label>
                    <input type="date" id="date" className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-medical-blue focus:ring-2 focus:ring-medical-blue/20 outline-none transition-all text-sm text-text-dark" data-id="element-421" />
                  </div>
                </div>

                <div data-id="element-422">
                  <label htmlFor="notes" className="block text-sm font-semibold text-text-dark mb-1.5" data-id="element-423">
                    Message / Special Notes
                  </label>
                  <textarea id="notes" rows={4} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-medical-blue focus:ring-2 focus:ring-medical-blue/20 outline-none transition-all text-sm resize-none" placeholder="Tell us about your specific requirements, shift times, or patient needs..." data-id="element-424"></textarea>
                </div>

                <button type="submit" className="w-full bg-medical-blue hover:bg-medical-blue-dark text-white font-bold py-3.5 rounded-xl transition-colors flex items-center justify-center gap-2 text-sm shadow-md" data-id="element-425">
                  <SendIcon className="w-4 h-4" data-id="element-426" />
                  Request Staff Now
                </button>
                <p className="text-center text-xs text-text-muted mt-3" data-id="element-427">
                  Your information is secure and will never be shared with third
                  parties.
                </p>
              </form>}
          </div>
        </div>
      </div>
    </section>;
}